<template>
  <div
    class="candidate-card absolute top-0 right-0 bottom-0 left-0 bg-gradient rounded-xxl"
    :class="{
      'swing-left': type === MatchType.Dislike && !dragging,
      'swing-up': type === MatchType.Love && !dragging,
      'swing-right': type === MatchType.Like && !dragging,
      'new-card': newCard,
      'fade-in': fadeIn,
      'pointer-events-none': matching
    }"
  >
    <div class="flex flex-col h-full items-stretch">
      <!-- Candidate Info -->
      <div class="flex flex-col">
        <box-image class="box-image flex flex-col justify-center">
          <p class="text-chplus-s" style="flex:0 0 1em">
            {{ $t("components.candidate.candidate_card.title") }}
          </p>
          <router-link
            class="flex-1 flex flex-row justify-center"
            :to="{
              name: 'candidate',
              params: {
                candidateId: candidate.id,
                electionId: activeElection.id,
                closeOnMatch: true
              }
            }"
          >
            <img
              :src="
                candidate.urlImage ||
                  require(`@/assets/images/genicon-candidate-unknown.svg`)
              "
              class="candidate-portrait rounded-full object-cover"
              draggable="false"
              v-fallback="
                require(`@/assets/images/genicon-candidate-unknown.svg`)
              "
            />
          </router-link>
        </box-image>
        <spider-title>
          <span class="text-chplus-l font-bold leading-none">
            <router-link
              :to="{
                name: 'candidate',
                params: {
                  candidateId: candidate.id,
                  electionId: activeElection.id,
                  closeOnMatch: true
                }
              }"
            >
              {{ candidate.firstName }} {{ candidate.lastName }}
            </router-link>
          </span>
          <span class="text-chplus-s">
            {{ $t("components.candidate.candidate_card.introduction_phrase") }}
          </span>
          <span
            class="text-chplus-l font-bold"
            v-if="containsSpiderData(candidate.smartSpider)"
          >
            {{ (candidate.matchPercent || 0).toFixed(0) }}%
          </span>
          <span class="text-chplus-l font-bold" v-else>…%</span>
        </spider-title>
      </div>
      <!-- Stamps -->
      <match-stamps :match-bus="stampBus" />
      <!-- Smart Spider -->
      <div class="w-full relative flex-grow">
        <!-- // cannot have anchor on smart spider because it prevents dragging the card
        <router-link
          v-if="candidate.smartSpider.reduce((a, b) => a + b, 0) > 0"
          :to="{ name: 'candidate', params: { candidateId: candidate.id, closeOnMatch: true } }"
        >-->
        <div
          v-if="containsSpiderData(candidate.smartSpider)"
          class="absolute top-0 left-0 w-full h-full flex flex-col justify-center"
        >
          <div class="mx-4 w-auto flex flex-row justify-center">
            <div
              ref="smart-spider-box"
              class="h-full flex flex-col justify-center"
            >
              <smart-spider
                :showLabels="true"
                :candidateId="candidate.id"
                :use-party-color="false"
                :show-background="true"
                :faded-background="true"
                :white-border="true"
                :glow="true"
              ></smart-spider>
            </div>
          </div>
        </div>
        <!--</router-link>-->
        <div
          v-else
          class="absolute text-white w-full h-full flex flex-col justify-center"
        >
          <template v-if="noInfo">
            <span class="text-chplus-s">
              {{
                $t("components.candidate.candidate_card.info_empty_profile_1")
              }}
            </span>
            <span class="text-chplus-regular font-bold">
              {{
                $t("components.candidate.candidate_card.info_empty_profile_2")
              }}
            </span>
          </template>
          <template v-else>
            <span class="text-chplus-s">
              {{
                $t(
                  "components.candidate.candidate_card.info_empty_questionnaire_1"
                )
              }}
            </span>
            <span class="text-chplus-regular font-bold">
              {{
                $t(
                  "components.candidate.candidate_card.info_empty_questionnaire_2"
                )
              }}
            </span>
          </template>
          <candidate-info-box
            class="my-2"
            :candidate="candidate"
            :light-theme="true"
            :short-version="true"
          />
          <span v-if="noInfo" class="text-chplus-s">{{
            $t(
              "components.candidate.candidate_card.no_additional_info_available"
            )
          }}</span>
          <span v-else class="text-chplus-s">{{
            $t("components.candidate.candidate_card.additional_info_available")
          }}</span>
        </div>
      </div>
      <!-- Evaluation Buttons -->
      <bottom-evaluation>
        <evaluation-buttons :candidate="candidate" @match="match" />
      </bottom-evaluation>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import SmartSpider from "../chart/SmartSpider";
import Vue from "vue";
import BoxImage from "@/components/layouts/BoxImage";
import SpiderTitle from "@/components/layouts/SpiderTitle";
import BottomEvaluation from "@/components/layouts/BottomEvaluation";
import EvaluationButtons from "@/components/buttons/EvaluationButtons";
import MatchStamps from "@/components/people/MatchStamps";
import CandidateInfoBox from "@/components/people/CandidateInfoBox";
import { CandidateUtility } from "@/utils/CandidateUtility";
import { MatchType } from "@/utils/MatchType";
import FallbackImg from "@/directives/FallbackImg";

export default {
  name: "CandidateCard",
  directives: {
    fallback: FallbackImg
  },
  components: {
    CandidateInfoBox,
    MatchStamps,
    EvaluationButtons,
    BottomEvaluation,
    SpiderTitle,
    BoxImage,
    SmartSpider
  },
  props: { candidateId: Number, dragBus: Vue },
  data: () => ({
    MatchType: MatchType,
    type: null,
    dragging: false,
    confidence: 0,
    newCard: true,
    fadeIn: false,
    stampBus: new Vue(),
    matching: false
  }),
  computed: {
    ...mapGetters("user", ["smartSpider"]),
    ...mapGetters("election", ["activeElection"]),
    candidate() {
      return this.$store.getters["candidate/candidate"](this.candidateId);
    },
    noInfo() {
      return CandidateUtility.noInfo(this.candidate);
    }
  },
  created() {
    this.dragBus.$on("dragmove", this.onDragMove);
    this.dragBus.$on("dragend", this.onDragEnd);
    setTimeout(() => (this.fadeIn = true), 10);
    setTimeout(() => {
      this.newCard = false;
      this.fadeIn = false;
    }, 310);
  },
  mounted() {
    window.addEventListener("resize", this.onWindowResize);
    this.onWindowResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.onWindowResize);
  },
  methods: {
    onWindowResize() {
      const smartSpiderBox = this.$refs["smart-spider-box"];
      if (!smartSpiderBox) {
        return;
      }
      const parentStyle = getComputedStyle(smartSpiderBox.parentNode);
      const fullAreaStyle = getComputedStyle(
        smartSpiderBox.parentNode.parentNode
      );
      const width = Math.min(
        parseInt(parentStyle.width),
        parseInt(fullAreaStyle.height)
      );
      smartSpiderBox.style.width = width + "px";
      smartSpiderBox.style.height = width + "px";
    },
    containsSpiderData(data) {
      return data && Object.values(data).reduce((a, b) => a + b, 0) > 0;
    },
    onDragMove(e) {
      let type = e.dragType;
      let confidence = e.confidence;
      this.dragging = true;
      this.type = type;
      this.confidence = confidence;
      this.stampBus.$emit("drag", { type: type, confidence: confidence });
    },
    onDragEnd() {
      this.dragging = false;
      this.type = null;
      this.confidence = 0;
      this.stampBus.$emit("dragend");
    },
    match(type) {
      if (this.matching) {
        return;
      }
      this.matching = true;
      this.type = type;
      this.confidence = 1;
      this.stampBus.$emit("match", type);
      setTimeout(() => {
        this.$emit("match", type);
        this.matching = false;
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
.bg-gradient {
  background: linear-gradient(
    0deg,
    #583952 0%,
    #394053 40%,
    #323849 70%,
    #25303e 100%
  );
}

.swing-left {
  transition: all 1000ms;
  transform: rotate(-8deg) translateX(-50%) !important;
  opacity: 0;
}

.swing-up {
  transition: all 1000ms;
  transform: translateY(-50%) !important;
  opacity: 0;
}

.swing-right {
  transition: all 1000ms;
  transform: rotate(8deg) translateX(50%) !important;
  opacity: 0;
}

.candidate-card.new-card {
  transform: scale(0.7);
  opacity: 0.4;
}

.candidate-card.new-card.fade-in {
  transition: opacity 300ms, transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  opacity: 1;
}

.candidate-portrait {
  width: 5.6rem;
  height: 5.6rem;
}
@media (min-width: 768px) {
  .candidate-portrait {
    width: 6.5rem;
    height: 6.5rem;
  }
}
@media (max-width: 380px), (max-height: 600px) {
  .candidate-portrait {
    width: 5.2rem;
    height: 5.2rem;
  }
  .box-image {
    height: 7.392rem;
    flex: 0 0 7.392rem;
  }
}
</style>
